import type { Account } from '@openx/types/account.ts';
import type { Session } from '@openx/types/session.ts';
import type { KyInstance } from 'ky';
import merge from 'lodash/merge';
import { storeLastValidInstanceName } from '../lib/oidc/oidcAuthInfo.ts';
import { USER_SESSION_LOCALSTORAGE_KEY } from '../session/constants.ts';
import { deriveSessionState } from './deriveSessionState.ts';
import { SessionInstanceUnavailableError } from './SessionInstanceUnavailableError.ts';
import type { SessionQueryData } from './types.ts';

/**
 * `/session` returns a trimmed account projection — it omits fields that only the account
 * resource carries, notably `reporting_api_enabled` (see DATAO-5804, which routed the publisher
 * T&C flow through the session fetch precisely to read that field). So the account detail is
 * fetched separately and merged in, matching the pre-React-Query boot path.
 */
export async function fetchSession(http: KyInstance): Promise<SessionQueryData> {
  const sessionResponse = await http.get('session').json<Session>();

  if (!sessionResponse.user) {
    throw new SessionInstanceUnavailableError();
  }

  if (!sessionResponse.user.account_id) {
    throw new Error('Invalid session: missing account id');
  }

  if (!sessionResponse.account) {
    throw new Error('Invalid session: missing account');
  }

  if (!sessionResponse.instance) {
    throw new Error('Invalid session: missing instance');
  }

  // There was initiative to remove `account` from session, revisit later if it's still necessary.
  const account = await fetchSessionAccount(http, sessionResponse.user.account_id);
  const session = account ? merge({}, sessionResponse, { account }) : sessionResponse;

  // NO-JIRA: Verify that this could be removed, as it's already a top-level object in the session property. Maybe rewire usage places.
  session.user.account = merge({}, session.user.account, session.account);

  localStorage.setItem(USER_SESSION_LOCALSTORAGE_KEY, JSON.stringify(session));
  storeLastValidInstanceName(sessionResponse.instance.name);

  return deriveSessionState(session);
}

/**
 * A missing or failed account detail fetch must not fail boot — the session payload alone is
 * enough for permissions and routing, so callers degrade to the trimmed account.
 */
async function fetchSessionAccount(http: KyInstance, accountId: string): Promise<Account | undefined> {
  try {
    const accountResponse = await http
      .get('account', { searchParams: { id: accountId } })
      .json<{ objects?: Account[] }>();

    return accountResponse?.objects?.[0];
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: account detail is supplementary; log and degrade
    console.error('fetch session account error', error);

    return undefined;
  }
}
